import {IAttributeTemplate} from '../schema';
import {ATTRIBUTE_NUMBER_MAX_DECIMAL} from '../constants';
import {bytes} from '@netx/core/lib/underscore-netx';

import base from './base';

const formatDecimalString = (
	rawValue: string,
	{
		maxFractionDigits,
		minFractionDigits = 0,
	}: {maxFractionDigits: number; minFractionDigits?: number},
) => {
	const trimmed = rawValue.trim();
	const match = trimmed.match(/^([+-]?)(\d*)(?:\.(\d*))?$/);

	if (!match) return rawValue;

	const sign = match[1] || '';
	const integerPart = match[2] || '0';
	const fractionPart = match[3] || '';

	let nextFraction = fractionPart;

	if (maxFractionDigits >= 0) {
		nextFraction = nextFraction.slice(0, maxFractionDigits);
	}

	if (minFractionDigits > nextFraction.length) {
		nextFraction = nextFraction.padEnd(minFractionDigits, '0');
	}

	return nextFraction.length > 0
		? `${sign}${integerPart}.${nextFraction}`
		: `${sign}${integerPart}`;
};

type Options = {
	noLocale?: boolean;
};

export const number = (
	value: any,
	{name, spec2, system}: Pick<IAttributeTemplate, 'name' | 'spec2' | 'system'>,
	opts: Options = {},
) => {
	if (value == null) return null;
	if (value === '') return '';

	// Do not parse into locale string
	const noLocale = opts.noLocale === true;
	const numberType = spec2;
	const maxDecimal =
		ATTRIBUTE_NUMBER_MAX_DECIMAL[
			numberType as keyof typeof ATTRIBUTE_NUMBER_MAX_DECIMAL
		] || 0;

	const parse = (value: any) => {
		if (typeof value === 'symbol') return '';
		// Prevent parsing very large numbers that got into the system via other means
		// the ui only allows for entry of up to a length of 11, so there are other issues
		// that will be presented to the user should they ever want to edit said value
		// if (value.length > 11) return value;
		const stringValue = typeof value === 'string' ? value : `${value}`;

		// `Unlimited` number type
		if (maxDecimal === Infinity) {
			return noLocale
				? stringValue
				: formatDecimalString(stringValue, {
						maxFractionDigits: 20, // Maximum allowed by toLocaleString
					});
		} else {
			return formatDecimalString(stringValue, {
				maxFractionDigits: maxDecimal,
				minFractionDigits: noLocale ? 0 : maxDecimal,
			});
		}
	};

	if (system) {
		// Normalize
		switch (name) {
			case 'fileSize':
			case 'fileSize2':
				value = bytes(value, true);
				break;
		}
	} else if (value != null) {
		value = Array.isArray(value) ? value.map(parse) : parse(value);
	}

	return base(value);
};

export default number;
